import { useState } from "react";

const roles = [
  {
    icon: "ri-user-3-line",
    label: "Student",
    color: "bg-amber-50 border-amber-200 text-amber-700",
    activeColor: "bg-amber-500 border-amber-500 text-white",
    desc: "Find tutors, study partners & mentors",
  },
  {
    icon: "ri-book-open-line",
    label: "Teacher",
    color: "bg-emerald-50 border-emerald-200 text-emerald-700",
    activeColor: "bg-emerald-500 border-emerald-500 text-white",
    desc: "Connect with students who need your expertise",
  },
  {
    icon: "ri-parent-line",
    label: "Parent",
    color: "bg-rose-50 border-rose-200 text-rose-700",
    activeColor: "bg-rose-500 border-rose-500 text-white",
    desc: "Support your child's academic journey",
  },
];

const features = [
  {
    icon: "ri-robot-2-line",
    title: "AI-Powered Matching",
    desc: "Our AI reads your profile, goals, and documents to find your perfect teacher, student, or support partner.",
    color: "text-amber-500",
    bg: "bg-amber-50",
  },
  {
    icon: "ri-file-upload-line",
    title: "Upload Your Documents",
    desc: "Share transcripts, essays, certificates — let others understand your background and what you need.",
    color: "text-emerald-600",
    bg: "bg-emerald-50",
  },
  {
    icon: "ri-wechat-line",
    title: "Exchange Contacts",
    desc: "Once matched, safely share your WeChat ID or email to start communicating directly.",
    color: "text-sky-500",
    bg: "bg-sky-50",
  },
  {
    icon: "ri-trophy-line",
    title: "Weekly Rewards",
    desc: "Log in every week to earn score points. The more you engage, the more your virtual farm grows!",
    color: "text-rose-500",
    bg: "bg-rose-50",
  },
];

const matchCards = [
  {
    name: "Ms. Sarah Chen",
    role: "Teacher",
    subject: "SAT Math & Reading",
    score: 98,
    avatar: "https://public.readdy.ai/ai/img_res/0cbd84757a3bbe4a72ca111e71274d07.jpg",
    tags: ["SAT Prep", "Calculus", "Essay Coaching"],
    match: 97,
  },
  {
    name: "James Park",
    role: "Student",
    subject: "Grade 11 — Ivy League Bound",
    score: 1480,
    avatar: "https://readdy.ai/api/search-image?query=young%20asian%20male%20high%20school%20student%20smiling%2C%20clean%20white%20background%2C%20portrait%20photo%2C%20casual%20academic%20wear%2C%20friendly%20expression&width=80&height=80&seq=comm2&orientation=squarish",
    tags: ["Study Partner", "AP Physics", "College Essays"],
    match: 94,
  },
  {
    name: "Linda Morrison",
    role: "Parent",
    subject: "Supporting Grade 10 Daughter",
    score: null,
    avatar: "https://readdy.ai/api/search-image?query=friendly%20caucasian%20mother%20smiling%20warmly%2C%20clean%20white%20background%2C%20portrait%20photo%2C%20natural%20lighting%2C%20supportive%20parent%20expression&width=80&height=80&seq=comm3&orientation=squarish",
    tags: ["College Prep", "Motivation", "Scholarship"],
    match: 91,
  },
];

export default function CommunitySection() {
  const [activeRole, setActiveRole] = useState(0);

  return (
    <section id="community" className="py-24 bg-gradient-to-b from-white to-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold tracking-widest uppercase mb-4">
            Community Hub
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-5"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Students, Teachers &amp; Parents —<br />
            <span className="text-amber-500">Together We Grow</span>
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed">
            Sign up as a student, teacher, or parent. Upload your profile, let our AI find your perfect match, and start building meaningful connections that last a lifetime.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left: Role selector + features */}
          <div>
            {/* Role Tabs */}
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">I am a...</p>
            <div className="flex gap-3 mb-10 flex-wrap">
              {roles.map((r, i) => (
                <button
                  key={r.label}
                  onClick={() => setActiveRole(i)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeRole === i ? r.activeColor : r.color
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={r.icon}></i>
                  </div>
                  {r.label}
                </button>
              ))}
            </div>
            <p className="text-gray-600 text-base mb-10 leading-relaxed">
              {roles[activeRole].desc} — create your profile, upload documents, and let our AI connect you with the right people.
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {features.map((f) => (
                <div key={f.title} className={`rounded-xl p-5 ${f.bg}`}>
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg mb-3 ${f.bg}`}>
                    <i className={`${f.icon} text-xl ${f.color}`}></i>
                  </div>
                  <h4 className="font-semibold text-gray-800 text-sm mb-1">{f.title}</h4>
                  <p className="text-gray-500 text-xs leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex gap-3 flex-wrap">
              <a
                href="/auth"
                className="px-6 py-3 rounded-full bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Join the Community
              </a>
              <a
                href="/community"
                className="px-6 py-3 rounded-full border border-gray-200 text-gray-700 text-sm font-semibold hover:border-amber-400 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Browse Matches
              </a>
            </div>
          </div>

          {/* Right: Match preview cards */}
          <div className="flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">AI-Suggested Matches for You</p>
            {matchCards.map((card) => (
              <div
                key={card.name}
                className="bg-white rounded-2xl p-5 border border-gray-100 flex items-start gap-4 hover:border-amber-200 transition-all duration-200 group"
              >
                <div className="w-14 h-14 flex-shrink-0 rounded-full overflow-hidden">
                  <img
                    src={card.avatar}
                    alt={card.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <h4 className="font-semibold text-gray-900 text-sm">{card.name}</h4>
                    <span className="flex-shrink-0 text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                      {card.match}% match
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mb-2">{card.subject}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {card.tags.map((tag) => (
                      <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <button className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-amber-50 text-amber-500 group-hover:bg-amber-500 group-hover:text-white transition-all cursor-pointer">
                  <i className="ri-arrow-right-line text-sm"></i>
                </button>
              </div>
            ))}

            {/* Weekly streak badge */}
            <div className="mt-2 bg-gradient-to-r from-amber-500 to-orange-400 rounded-2xl p-5 text-white flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20 flex-shrink-0">
                <i className="ri-fire-line text-2xl"></i>
              </div>
              <div>
                <p className="font-bold text-base">Weekly Login Streak</p>
                <p className="text-white/80 text-sm">Log in every week to earn score points &amp; grow your farm. Don&apos;t break the streak!</p>
              </div>
              <div className="ml-auto flex-shrink-0 text-center">
                <p className="text-3xl font-bold">🔥 7</p>
                <p className="text-white/70 text-xs">weeks</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
